import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { axiosInt, API_CORE_URL, type IModelInstance } from '../../..';

export const FETCH_IDMPS_KEY = 'fetchIdmps';

interface CursorPageResponse<T> {
  data: T[];
  hasNext: boolean;
  hasPrev: boolean;
}

const fetchIdmpsApi = async (uuids: string[]): Promise<IModelInstance[]> => {
  if (uuids.length === 0) return [];
  // Tomcat rejects raw `[` / `]` (RFC 7230) — encode the bracketed key.
  const key = encodeURIComponent('modelInstanceIds[]');
  const params = uuids.map(u => `${key}=${encodeURIComponent(u)}`).join('&');
  const response = await axiosInt.get<CursorPageResponse<IModelInstance>>(
    `${API_CORE_URL}/model-instances/cursor-pagination?${params}`
  );
  return response.data.data;
};

/**
 * Fetches a batch of IDMP model instances and exposes them keyed by id.
 * Sorted-uuid query key makes the cache stable across input reorderings.
 */
export const useFetchIdmps = (uuids: string[]) => {
  const cacheKey = useMemo(() => [...uuids].sort().join(','), [uuids]);

  const { data, isLoading, error } = useQuery({
    queryKey: [FETCH_IDMPS_KEY, cacheKey],
    queryFn: () => fetchIdmpsApi(uuids),
    enabled: uuids.length > 0,
    placeholderData: prev => prev,
  });

  const idmpById = useMemo(() => {
    const map: Record<string, IModelInstance> = {};
    for (const inst of data ?? []) map[inst.id] = inst;
    return map;
  }, [data]);

  return {
    idmpById,
    isLoadingIdmps: isLoading,
    errorFetchingIdmps: error,
  };
};
